import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import ProjectsShowcase from '../components/common/ProjectsShowcase';
import { BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { galleryImages } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const GalleryPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://basinplumbing.com/' },
    { name: 'Gallery', url: 'https://basinplumbing.com/gallery/' }
  ];

  return (
    <>
      <Seo {...seoConfig.gallery} />
      <BreadcrumbSchema items={breadcrumbs} />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#1a3a5c] to-[#0f2438]">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="text-sm text-blue-200 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <span className="mx-2">/</span>
              <span className="text-white">Gallery</span>
            </nav>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 bg-[#e85a24] rounded-xl flex items-center justify-center">
                <Camera className="text-white" size={28} />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Our Work
              </h1>
            </div>
            <p className="text-xl text-blue-100 max-w-2xl">
              Browse through photos of our completed plumbing projects. 
              Quality workmanship you can see.
            </p>
          </div>
        </section>

        {/* Captioned Recent Projects (local-SEO proof) */}
        <ProjectsShowcase />

        {/* Gallery Grid */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {galleryImages.map((image, index) => (
                <div 
                  key={index}
                  onClick={() => openLightbox(index)}
                  className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group shadow-lg hover:shadow-xl transition-all"
                >
                  <img 
                    src={image.src} 
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-white text-sm font-medium">{image.alt}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {lightboxOpen && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
            <button 
              onClick={closeLightbox}
              className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X size={24} />
            </button>
            
            <button 
              onClick={prevImage}
              className="absolute left-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronLeft size={24} />
            </button>
            
            <button 
              onClick={nextImage}
              className="absolute right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <ChevronRight size={24} />
            </button>

            <div className="max-w-4xl max-h-[80vh] px-16">
              <img 
                src={galleryImages[currentImage].src} 
                alt={galleryImages[currentImage].alt}
                className="max-w-full max-h-[80vh] object-contain rounded-lg"
              />
              <p className="text-white text-center mt-4">
                {galleryImages[currentImage].alt}
              </p>
            </div>

            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {galleryImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    currentImage === index ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* CTA Section */}
        <CTASection 
          title="Ready to Start Your Project?"
          subtitle="Get in touch for a free estimate on your plumbing project."
        />
      </main>

      <Footer />
    </>
  );
};

export default GalleryPage;
